// pages/Login.tsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "wouter";
import Layout from "@/components/Layout";
import SEO from '@/components/SEO'; // SEO: login is private entry -> noindex,nofollow
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Laptop, RefreshCw, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function Login() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // 🟢 ვხმარობთ შენს useAuth ჰუკს:
  // /api/auth/login — loginMutation
  // /api/auth/resend-verification — resendCodeMutation
  // /api/auth/verify-code — verifyCodeMutation
  const {
    loginMutation,
    isAuthenticated,
    verifyDeviceOtpMutation,
    resendDeviceOtpMutation,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'login' | 'otp'>('login');
  const [otpCode, setOtpCode] = useState("");
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [justRegistered, setJustRegistered] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const otpInputRefs = Array.from({ length: 6 }).map(() => useRef<HTMLInputElement | null>(null));

  // resend cooldown
  const RESEND_SECONDS = 120;
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (isAuthenticated) {
      setLocation("/dashboard");
    }
  }, [isAuthenticated, setLocation]);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    // Detect redirect after registration
    try {
      if (localStorage.getItem('registrationSuccess')) {
        setJustRegistered(true);
        localStorage.removeItem('registrationSuccess');
        setTimeout(() => setJustRegistered(false), 2000);
      }
    } catch {}
  }, []);

  // Friendly error message mapper to avoid exposing backend internals
  const toFriendlyError = useCallback((err: any, fallback: string) => {
    const raw = String(err?.message || err || "");
    const lower = raw.toLowerCase();
    if (lower.includes('invalid code') || /code.*invalid/.test(lower)) return 'Please check the 6 digits and try again.';
    if (lower.includes('expired')) return 'Code expired. Request a new one.';
    if (lower.includes('missing session')) return 'Session expired. Please sign in again.';
    if (lower.includes('blocked') || lower.includes('banned')) return 'This user is blocked';
    if (lower.includes('too many') || lower.includes('rate limit')) return 'Too many attempts. Please wait and try again.';
    // hide any raw server / stack specifics
    if (/\b(500|traceback|stack|greenlet|await_only)\b/.test(lower)) return fallback;
    // Do NOT surface raw; always fallback unless explicitly whitelisted above
    return fallback;
  }, []);

  // 🟢 ლოგინი /api/auth/login
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    loginMutation.mutate(
      { email, password, rememberMe },
      {
        onSuccess: (resp: any) => {
          setLoginError(null);
          if (resp?.requires_otp || resp?.session_status === 'pending') {
            setSessionStatus('pending');
            setStep('otp');
            setOtpCode("");
            setOtpError(null);
            // Start cooldown immediately so user sees "Resend in 2:00" right away
            setCooldown(RESEND_SECONDS);
            toast({
              title: 'Device Verification Required',
              description: 'Enter the 6-digit code sent to your email.',
            });
            // focus first digit shortly
            setTimeout(() => otpInputRefs[0].current?.focus(), 80);
            return;
          }
          if (rememberMe) localStorage.setItem("rememberedEmail", email); else localStorage.removeItem("rememberedEmail");
          toast({ title: "Welcome back!", description: "You've been successfully signed in.", className: "toast-success" });
          setLocation('/dashboard');
        },
        onError: (error: any) => {
          const mapped = toFriendlyError(error, 'Email or password is incorrect.');
            if (mapped === 'This user is blocked') {
              setLoginError(mapped);
            } else {
              setLoginError('Email or password is incorrect.');
            }
        }
      }
    );
  };
  const handleOtpVerify = useCallback(() => {
    if (otpCode.length !== 6) {
      setOtpError('Please check the 6 digits and try again.');
      return;
    }
    setOtpError(null);
    verifyDeviceOtpMutation.mutate({ code: otpCode }, {
      onSuccess: () => {
        setSessionStatus('active');
        toast({ title: 'Device Verified', description: 'Session activated.' });
        setLocation('/dashboard');
      },
      onError: (e: any) => {
        const msg = toFriendlyError(e, 'Invalid code. Please try again.');
        setOtpError(msg);
      }
    });
  }, [otpCode, verifyDeviceOtpMutation, setLocation, toast, toFriendlyError]);

  const handleResendOtp = () => {
    if (cooldown > 0) return;
    resendDeviceOtpMutation.mutate(undefined, {
      onSuccess: () => {
        setCooldown(RESEND_SECONDS);
        toast({ title: 'Code Sent', description: 'Check your email for a new code.' });
      },
      onError: (e: any) => {
        toast({
          title: 'Resend Failed',
          description: toFriendlyError(e, 'Server error while resending code. Please try again shortly.'),
          variant: 'destructive',
        });
      }
    });
  };

  // OTP segmented inputs
  const handleDigitChange = (index: number, val: string) => {
    if (!/^[0-9]?$/.test(val)) return;
    const digits = otpCode.split("");
    digits[index] = val;
    const newCode = digits.join("").padEnd(6, "").slice(0,6);
    setOtpCode(newCode);
    setOtpError(null);
    if (val && index < 5) otpInputRefs[index + 1].current?.focus();
  };
  const handleDigitKey = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    } else if (e.key === 'Enter') {
      handleOtpVerify();
    }
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const isLoggingIn = loginMutation.isPending;
  const isVerifyingOtp = verifyDeviceOtpMutation.isPending;
  const isResendingOtp = resendDeviceOtpMutation.isPending;

  return (
    <Layout>
      <SEO title="Login | TradeSagePro" description="Secure login to access AI market research analysis and your personalized dashboard." canonical="https://tradesagepro.com/login" noindex nofollow />
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-white">Welcome Back</CardTitle>
              <CardDescription className="text-[hsl(210,9%,80%)]">
                Sign in to your TradeSagePro account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {justRegistered && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-green-500/40 bg-green-500/15 p-3 text-sm text-white animate-fade-in">
                  <CheckCircle2 className="w-4 h-4 mt-[2px] text-green-300" />
                  <div>
                    <p className="font-semibold">You have successfully registered.</p>
                    <p className="text-xs opacity-80">Please sign in to continue.</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
        <div>
                  <Label htmlFor="email" className="text-white">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (loginError) setLoginError(null);
                      setOtpCode("");
                      setCooldown(0);
                      if (step === 'otp') { setStep('login'); setSessionStatus(null); }
                    }}
                    placeholder="you@example.com"
                    required
                    className="bg-[hsl(226,26%,7%)] border-gray-600 text-white placeholder:text-gray-400 focus:border-[hsl(193,100%,50%)] focus:ring-[hsl(193,100%,50%)]"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); if (loginError) setLoginError(null); }}
                      placeholder="••••••••"
                      required
                      className="pr-10 bg-[hsl(226,26%,7%)] border-gray-600 text-white placeholder:text-gray-400 focus:border-[hsl(193,100%,50%)] focus:ring-[hsl(193,100%,50%)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute inset-y-0 right-0 px-2 flex items-center text-gray-400 hover:text-white"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="remember"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-[hsl(193,100%,50%)] bg-gray-700 border-gray-600 rounded focus:ring-[hsl(193,100%,50%)]"
                  />
                  <label htmlFor="remember" className="text-sm text-white cursor-pointer">
                    Remember Me
                  </label>
                </div>

                {loginError && (
                  <div className={`text-sm border rounded px-3 py-2 -mb-1 ${
                    loginError === 'This user is blocked' 
                      ? 'bg-red-600/30 border-red-500/70 text-red-200' 
                      : 'bg-red-600/20 border-red-500/50 text-white'
                  }`}>
                    {loginError}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:bg-[hsl(193,90%,45%)] hover:scale-105 transition-all duration-300 font-semibold"
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-[hsl(226,26%,7%)] border-t-transparent rounded-full animate-spin"></div>
                      <span>Signing In...</span>
                    </div>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
              {step === 'otp' && (
                <div className="mt-10 rounded-lg border border-blue-500/40 bg-blue-500/10 p-5 space-y-4 animate-slide-in">
                  <div className="flex items-center gap-2 text-blue-300 text-sm font-medium">
                    <Laptop className="w-4 h-4" />
                    <span>Verify New Device</span>
                  </div>
                  <p className="text-xs text-[hsl(210,9%,70%)] leading-relaxed">
                    A 6-digit code was sent to <span className="font-semibold text-[hsl(193,100%,60%)]">{email}</span>. Enter it below to activate this browser.
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    {otpInputRefs.map((ref, i) => (
                      <input
                        key={i}
                        ref={ref}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={otpCode[i] || ''}
                        onChange={(e) => handleDigitChange(i, e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => handleDigitKey(i, e)}
                        className={`w-10 h-12 text-center text-lg font-semibold tracking-wider rounded-md border bg-[hsl(226,26%,7%)] focus:outline-none focus:ring-2 focus:ring-[hsl(193,100%,50%)] transition-colors ${otpError ? 'border-red-500' : 'border-gray-600 text-white'}`}
                        aria-label={`Digit ${i + 1}`}
                      />
                    ))}
                  </div>
                  {otpError && <div className="text-xs mt-1 px-2 py-1 rounded bg-red-600/20 border border-red-500/40 text-white">{otpError}</div>}
                  <div className="flex gap-2 items-center">
                    <Button
                      type="button"
                      onClick={handleOtpVerify}
                      disabled={isVerifyingOtp || otpCode.length !== 6}
                      className="flex-1 bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:bg-[hsl(193,90%,45%)] hover:scale-105 transition-all duration-300 font-semibold"
                    >
                      {isVerifyingOtp ? 'Verifying...' : 'Verify'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isResendingOtp || cooldown > 0}
                      onClick={handleResendOtp}
                      className="px-3 text-xs"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      {isResendingOtp
                        ? 'Sending'
                        : cooldown > 0
                        ? `Resend in ${formatTime(cooldown)}`
                        : 'Resend'}
                    </Button>
                  </div>
                  <div className="flex justify-between items-center text-[10px] text-[hsl(210,9%,55%)]">
                    <span>Status: {sessionStatus || '—'}</span>
                    <button
                      type="button"
                      onClick={() => { setStep('login'); setOtpCode(''); setOtpError(null); setSessionStatus(null); }}
                      className="text-[hsl(193,100%,55%)] hover:underline"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {step === 'login' && (
              <div className="flex justify-between items-center mt-4">
                <Link
                  href="/forgot-password"
                  className="text-sm text-[hsl(193,100%,50%)] hover:text-[hsl(193,90%,45%)] hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              )}

              {step === 'login' && (
              <div className="text-center mt-6 pt-4 border-t border-gray-600">
                <p className="text-sm text-[hsl(210,9%,70%)]">
                  Don't have an account?{" "}
                  <Link
                    href="/register"
                    className="text-[hsl(193,100%,50%)] hover:text-[hsl(193,90%,45%)] hover:underline font-medium transition-colors"
                  >
                    Sign up
                  </Link>
                </p>
              </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}